import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, ClipboardPaste, X, Image as ImageIcon, Loader2 } from "lucide-react";

const MAX_IMAGES = 5;
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB

// Utility — File → data URL
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Raise Issue dialog. Users can:
 *   • Type a description of the issue
 *   • Paste screenshots from the clipboard (Ctrl+V) OR click "Paste image"
 *     to trigger a paste explicitly
 *
 * On submit → POST /api/tasks/:task_id/issues with description + images
 * (base64 data URLs). Backend enforces a 5-image / 2-MB-each cap. Images
 * auto-delete after 7 days; issues auto-delete after 14 days.
 *
 * Props:
 *   open, onOpenChange, task, onSaved
 */
export default function RaiseIssueDialog({ open, onOpenChange, task, onSaved, issuesBase }) {
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]); // [{ id, dataUrl, filename, size }]
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  // Reset every time we open
  useEffect(() => {
    if (open) {
      setDescription("");
      setImages([]);
      setSaving(false);
      // Focus textarea on next tick so users can start typing OR paste
      setTimeout(() => textareaRef.current?.focus(), 60);
    }
  }, [open]);

  // Global paste handler while the dialog is open — captures image
  // clipboard data anywhere inside the dialog (including from the
  // textarea).
  useEffect(() => {
    if (!open) return;
    const onPaste = async (e) => {
      const items = e.clipboardData?.items || [];
      const imgFiles = [];
      for (const it of items) {
        if (it.kind === "file" && (it.type || "").startsWith("image/")) {
          const f = it.getAsFile();
          if (f) imgFiles.push(f);
        }
      }
      if (imgFiles.length === 0) return; // let the text paste flow normally
      e.preventDefault();
      await addFiles(imgFiles);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, images.length]);

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (images.length >= MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images per issue`);
      return;
    }
    const remaining = MAX_IMAGES - images.length;
    const slice = Array.from(files).slice(0, remaining);
    const oversized = slice.filter((f) => f.size > MAX_BYTES);
    if (oversized.length > 0) {
      toast.error(`Each image must be under ${(MAX_BYTES / (1024 * 1024)).toFixed(0)} MB`);
    }
    const good = slice.filter((f) => f.size <= MAX_BYTES);
    const dataUrls = await Promise.all(good.map(async (f) => ({
      id: crypto.randomUUID(),
      dataUrl: await fileToDataUrl(f),
      filename: f.name || "paste.png",
      size: f.size,
    })));
    setImages((prev) => [...prev, ...dataUrls]);
    if (files.length > remaining) toast.warning(`Only added ${remaining} — max is ${MAX_IMAGES}`);
  };

  const removeImage = (id) => setImages((prev) => prev.filter((x) => x.id !== id));

  const submit = async () => {
    const desc = description.trim();
    if (!desc) {
      toast.error("Please describe the issue");
      textareaRef.current?.focus();
      return;
    }
    if (!task?.id) return;
    setSaving(true);
    try {
      const payload = {
        description: desc,
        images: images.map((i) => ({ data: i.dataUrl, filename: i.filename })),
      };
      await api.post(issuesBase ? `${issuesBase}/tasks/${task.id}/issues` : `/tasks/${task.id}/issues`, payload);
      toast.success("Issue raised — task pinned to the top");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="raise-issue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Raise an issue
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Describe what's wrong with this task. You can paste screenshots directly (Ctrl+V).
            Images are automatically deleted after 7 days; the issue record after 14 days.
          </DialogDescription>
        </DialogHeader>

        {task && (
          <div className="text-xs text-slate-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            <span className="font-semibold text-rose-800">Task:</span>{" "}
            <span data-testid="raise-issue-task-title">{task.keyword || task.title || "(untitled)"}</span>
          </div>
        )}

        <div>
          <label className="text-xs font-medium text-slate-700 mb-1 block">Description</label>
          <Textarea
            ref={textareaRef}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="What's the issue? (e.g. broken draft link, wrong publisher assigned, etc.)"
            className="text-sm"
            data-testid="raise-issue-description"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-slate-700">Screenshots</label>
            <span className="text-[11px] text-slate-500">{images.length}/{MAX_IMAGES} • paste with Ctrl+V</span>
          </div>
          <div
            className="border border-dashed border-slate-300 rounded-md p-3 bg-slate-50/60"
            data-testid="raise-issue-drop-area"
          >
            {images.length === 0 ? (
              <div className="text-center text-xs text-slate-500 py-6 select-none">
                <ImageIcon className="h-6 w-6 mx-auto mb-1 text-slate-400" />
                Paste an image here <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono">Ctrl</kbd>+<kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono">V</kbd>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {images.map((im) => (
                  <div key={im.id} className="relative group border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={`raise-issue-image-${im.id}`}>
                    {/* eslint-disable-next-line */}
                    <img src={im.dataUrl} alt="pasted screenshot" className="w-full h-24 object-cover" />
                    <button
                      onClick={() => removeImage(im.id)}
                      className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                      title="Remove"
                      data-testid={`raise-issue-remove-${im.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="text-[10px] text-slate-500 px-1 py-0.5 truncate">{im.filename}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={saving} data-testid="raise-issue-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !description.trim()}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="raise-issue-submit"
          >
            {saving ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Raising…</>) : (<><ClipboardPaste className="h-3.5 w-3.5 mr-1.5" /> Raise issue</>)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
